/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { guestIdentityLoginFetcher } from '@/data/Content/GuestFetcher';
import { useNotifications } from '@/data/Content/Notifications';
import {
	addPaymentInstructionFetcher,
	deleteAllPaymentInstructionFetcher,
} from '@/data/Content/Payment';
import { DATA_KEY, fetcher, getCart } from '@/data/Content/_Cart';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { ORDER_CONFIGS, SHIP_MODE_CODE_PICKUP, UNITLESS_UNIT_ONE } from '@/data/constants/order';
import { EventsContext, EventsContextType } from '@/data/context/events';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { PersonCheckoutProfilesItem } from '@/data/types/CheckoutProfiles';
import { ErrorType } from '@/data/types/Error';
import { GTMCartViewContextData } from '@/data/types/GTM';
import { Order, OrderItem } from '@/data/types/Order';
import { checkoutProfilePaymentConstructor } from '@/data/utils/checkoutProfilePaymentConstructor';
import { extractResponseError } from '@/data/utils/extractResponseError';
import { dFix } from '@/data/utils/floatingPoint';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { error as logError } from '@/data/utils/loggerUtil';
import { processError, isErrorType } from '@/data/utils/processError';
import { customTransactionsAssignedPromotionCode } from 'integration/generated/customTransactions';
import {
	transactionsAssignedPromotionCode,
	transactionsCart,
	transactionsGuestIdentity,
} from 'integration/generated/transactions';
import {
	CartRewardOption,
	ComIbmCommerceRestOrderHandlerCartHandlerAddOrderItemBodyDescription,
	ComIbmCommerceRestOrderHandlerCartHandlerOrderWithOrderItemContainer,
	ComIbmCommerceRestOrderHandlerCartHandlerUpdateOrderItemBodyDescription,
} from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { isEmpty, partition } from 'lodash';
import { GetServerSidePropsContext } from 'next';
import {
	ChangeEvent,
	KeyboardEvent,
	MouseEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import useSWR from 'swr';
import { useProducts } from '@/data/Content/Products';
import { WC_SESSION_ESTABLISHED } from '@/data/constants/cookie';
import { useCookies } from 'react-cookie';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { LegoLoyaltyCartValues } from './LegoLoyalty';
import { STORE_ID } from '@/data/config/STORE_ID';
import { DONATE_ITEM, FREE_GIFT, REMOVE } from '@/data/constants/cart';
import { getProductsWithAttribute, hasProductsWithAttributesMap } from '@/utils/productAttributes';

export const BASE_ADD_2_CART_BODY = {
	orderId: '.',
	x_calculateOrder: ORDER_CONFIGS.doNotCalculateOrder,
	x_calculationUsage: ORDER_CONFIGS.calculationUsage,
	x_inventoryValidation: ORDER_CONFIGS.inventoryValidation.toString(),
	orderItem: [],
};

export type AddToCartResponse =
	ComIbmCommerceRestOrderHandlerCartHandlerOrderWithOrderItemContainer;

export const addToCartFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestOrderHandlerCartHandlerAddOrderItemBodyDescription,
		params: RequestParams
	) => {
		try {
			return await transactionsCart(pub).cartAddOrderItem(storeId, query, data, params);
		} catch (error) {
			const er = extractResponseError(error as TransactionErrorResponse);
			if (er?.errorKey === 'USR.CWXFR0130E' || er?.errorCode === 'CWXFR0268E') {
				await transactionsGuestIdentity(true).guestIdentityLogin(
					storeId,
					{
						updateCookies: true,
					} as any,
					undefined,
					params
				);
				return await transactionsCart(pub).cartAddOrderItem(storeId, query, data, params);
			} else {
				throw error;
			}
		}
	};

/**
 * @param isGenericUser - if true, will attempt to login as a guest user before adding to cart
 */
export const addToCartFetcherV2 =
	(isGenericUser = false) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestOrderHandlerCartHandlerAddOrderItemBodyDescription,
		params: RequestParams
	) => {
		if (isGenericUser) {
			await guestIdentityLoginFetcher(true)(storeId, params);
		}
		return await transactionsCart(true).cartAddOrderItem(storeId, query, data, params);
	};

export const updateCartFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestOrderHandlerCartHandlerUpdateOrderItemBodyDescription,
		params: RequestParams
	) =>
		await transactionsCart(pub).cartUpdateOrderItem(storeId, query, data, params);

export const addRemoveB2BPromoCode =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			storeId: string,
			query: { [key: string]: string | boolean | ID[] | number } = {},
			params: RequestParams
		) => {
			try {
				return await customTransactionsAssignedPromotionCode(pub).cartSelfAssignedPromotionCodeRemoveB2BPromocodeDelete(
					storeId,
					query,
					params
				);
			} catch (e) {
				logError(context?.req, 'Cart: promoCodeRemover: error: %o', e);
				return undefined;
			}
		};


export const promoCodeApplicator =
	(pub: boolean) =>
	async (
		storeId: string,
		promoCode: string,
		query: { [key: string]: string | boolean | ID[] | number } = {},
		params: RequestParams
	) =>
		await transactionsAssignedPromotionCode(pub).cartSelfAssignedPromotionCodeCreate(
			storeId,
			{ promoCode },
			query,
			params
		);
// TODO: Promotion defect HCLRUBY-164
export const promoCodeRemover =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		promoCode: string,
		query: { [key: string]: string | boolean | ID[] | number } = {},
		params: RequestParams
	) => {
		try {
			return await transactionsAssignedPromotionCode(pub).cartSelfAssignedPromotionCodeDelete(
				storeId,
				promoCode,
				query,
				params
			);
		} catch (e) {
			logError(context?.req, 'Cart: promoCodeRemover: error: %o', e);
			return undefined;
		}
	};

type ProfileApplierProps = {
	profile: PersonCheckoutProfilesItem;
	order?: Order;
	storeId?: string;
	langId?: string;
	cvv?: string;
	params: RequestParams;
};

export const profileBillingApplier = async ({
	profile,
	order,
	storeId = '',
	langId = '',
	cvv,
	params,
}: ProfileApplierProps) => {
	await deleteAllPaymentInstructionFetcher(true)(storeId, { langId }, params);
	await addPaymentInstructionFetcher(true)(
		storeId,
		{ langId },
		checkoutProfilePaymentConstructor(order as Order, profile, cvv),
		params
	);
	return await fetcher(true)(storeId, { langId, sortOrder: 'desc' }, params);
};

const profileShippingApplier = async ({
	profile,
	storeId = '',
	langId = '',
	params,
}: ProfileApplierProps) => {
	const data = {
		toOrderId: '.',
		payInfoFrom: profile.xchkout_ProfileId,
		shippingAddressFromOrderProfile: '1',
		shippingModeFromOrderProfile: '1',
	};
	const calculationUsageId: any = ORDER_CONFIGS.calculationUsage.split(',');

	// copy profile info into order (clean payments first), calculate and add payment-info
	await transactionsCart(true).cartCopyOrder(storeId, undefined, data, params);
	await transactionsCart(true).cartCalculateOrder1(
		storeId,
		undefined,
		{ calculationUsageId },
		params
	);
	return await fetcher(true)(storeId, { langId, sortOrder: 'desc' }, params);
};

const getCount = (items?: OrderItem[]) =>
	items?.reduce(
		(previousCount, orderItem) =>
			orderItem.UOM === UNITLESS_UNIT_ONE
				? previousCount + dFix(orderItem.quantity ?? '0', 0)
				: previousCount + 1,
		0
	) ?? 0;

export type PromoCodeState = {
	code?: string;
	error: boolean;
};

export const useCartSWRKey = (): [any, string] | null => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const [cookies] = useCookies([WC_SESSION_ESTABLISHED]);
	const isWcSessionEstablished = !!cookies[WC_SESSION_ESTABLISHED];
	return storeId && isWcSessionEstablished ? [shrink({ storeId, query: { langId, sortOrder: 'desc' } }), DATA_KEY] : null;
};

export const useCart = () => {
	const { onCartView, onCartPageView, onEmptyCart } = useContext(EventsContext);
	const router = useNextRouter();
	const { user } = useUser();
	const [promoCode, setPromoCode] = useState<PromoCodeState>({ code: '' } as PromoCodeState);
	const { settings } = useSettings();
	const swrKey = useCartSWRKey();

	const localRoutes = useLocalization('Routes');
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const {
		data: cart,
		error,
		mutate,
		isValidating,
		isLoading,
	} = useSWR(
		swrKey,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props);
			const { storeId, query } = expanded;
			return fetcher(true)(storeId, query, params);
		},
		{ revalidateOnFocus: false }
	);
	const [waiting, setWaiting] = useState<boolean>(false);

	const [data, setData] = useState<Order | undefined>(() => cart);
	const count = useMemo(() => getCount(data?.orderItem), [data?.orderItem]);

	const { products : productsInOrder } = useProducts(data?.orderItem?.map(o=>o.xitem_parentPartNumber)??[], 'partNumber');

	const { isOrderHasPickupOnlyItem, isOrderHasPresaleItem } = useMemo(()=>{
		const attrFlags = hasProductsWithAttributesMap(
			productsInOrder, [ "ITNL_PickupInStoreOnly", "ITNL_ReleaseDate "]
		);
		return {
			isOrderHasPickupOnlyItem : attrFlags.ITNL_PickupInStoreOnly,
			isOrderHasPresaleItem: attrFlags.ITNL_ReleaseDate
		}
	},[productsInOrder]);

	const checkoutByType = useCallback(
		async (profile?: PersonCheckoutProfilesItem) => {
			setWaiting(true);
			try {
				let query;
				if (profile) {
					const { storeId, defaultLanguage: langId } = settings;
					let order = await profileShippingApplier({ profile, storeId, params });
					order = await profileBillingApplier({ profile, order, langId, storeId, params });
					await mutate(order);
					query = { profile: profile.xchkout_ProfileId };
				}
				await router.push({ pathname: localRoutes.CheckOut.route.t(), query });
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			} finally {
				setWaiting(false);
			}
		},
		[router, localRoutes, settings, params, mutate, notifyError]
	);

	const checkoutCommon = useCallback(
		async (profile?: PersonCheckoutProfilesItem) => {
			const isLoggedIn = user?.isLoggedIn ?? false;
			if (isLoggedIn) {
				await checkoutByType(profile);
			} else {
				router.push({ pathname: localRoutes.Login.route.t(), query: { flow: 'checkout' } });
			}
		},
		[localRoutes, router, user, checkoutByType]
	);
	const checkout = useCallback(async () => await checkoutCommon(), [checkoutCommon]);

	const onFullCartCheckout = useCallback(
		(profile?: PersonCheckoutProfilesItem) => async () => await checkoutCommon(profile),
		[checkoutCommon]
	);

	const continueShopping = () => router.push('/');

	const canContinue = () => !waiting;

	const orderItem = {
		quantity: data?.orderItem?.at(0)?.quantity.toString()??'1',
		orderItemId:data?.orderItem?.at(0)?.orderItemId,
	}
	
	const oiData = {
		orderId: '.',
		x_calculateOrder: ORDER_CONFIGS.calculateOrder,
		orderItem: [orderItem],
		x_calculationUsage: ORDER_CONFIGS.calculationUsage,
		x_inventoryValidation: ORDER_CONFIGS.inventoryValidation.toString(),
		x_source: ORDER_CONFIGS.source,
	};

	const _applyPromoCode = async () => {
		if (promoCode.code) {
			try {
				await promoCodeApplicator(true)(
					settings?.storeId as string,
					promoCode.code as string,
					undefined,
					params
				);
				onResetPromoCodeError();
				await updateCartFetcher(true)('11601', {}, oiData, params);
				mutate();
			} catch (e) {
				setPromoCode((prev) => ({ ...prev, error: true }));
				const error: TransactionErrorResponse | ErrorType = processError(
					e as TransactionErrorResponse
				);
				console.log("error  is ", error);
				if (isErrorType(error) && error.error.errorKey === "_ERR_PROMOTION_PER_SHOPPER_LIMIT_EXCEEDED_TREAZURE") {
					console.log("error exceed is ", error);
					notifyError({type:"common-error", messageKey:"_ERR_PROMOTION_PER_SHOPPER_LIMIT_EXCEEDED_TREAZURE", 
						errorParameters: [error.error.errorParameters]
					} as unknown as ErrorType);
					return;
				} else if (isErrorType(error) && error.error.errorKey === "_ERR_PROMOTION_CONDITIONS_NOT_MET") {
					console.log("error conditions is ", error);
					notifyError({
						type: "common-error", messageKey: "_ERR_PROMOTION_CONDITIONS_NOT_MET",
						errorParameters: [error.error.errorParameters]
					} as unknown as ErrorType);
					return;
				} else if (isErrorType(error) && error.error.errorKey === "ERR_PROMOTION_CODE_INVALID") {
					notifyError({
						type: "common-error", messageKey: "ERR_PROMOTION_CODE_INVALID",
						errorParameters: [error.error.errorParameters]
					} as unknown as ErrorType);
					return;
				} else if (isErrorType(error) && error.error.errorKey === "ERR_PROMOTION_CODE_DUPLICATED") {
					notifyError({
						type: "common-error", messageKey: "ERR_PROMOTION_CODE_DUPLICATED_-1150",
						errorParameters: [error.error.errorParameters]
					} as unknown as ErrorType);
					return;
					}
				return e as TransactionErrorResponse;
			}
		}
	};

	const onPromoCodeRemove = async (code?: string) => {
		const rc = await promoCodeRemover(true)(
			settings?.storeId as string,
			code as string,
			undefined,
			params
		);
		await updateCartFetcher(true)('11601', {}, oiData, params);
		if (!rc) {
			setPromoCode((prev) => ({ ...prev, error: true }));
		} else {
			onResetPromoCodeError();
			mutate();
		}
	};

	const onPromoCodeApply = async (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		_applyPromoCode();
	};

	const onPromoCodeApplyByKey = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') {
			_applyPromoCode();
		}
	};

	// state updaters
	const onPromoCodeChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		setPromoCode((prev) => ({ ...prev, code: e.target.value }));
	const onResetPromoCodeError = () => setPromoCode((prev) => ({ ...prev, code: '', error: false }));

	const onCartPageViewEvent = useCallback(
		() =>
			onCartPageView({
				gtm: {
					pagePath: router.asPath,
					isLoggedIn: !!user?.isLoggedIn,
					userId: user?.userId as string,
					orgName: '', // TODO: specify selected org-name
					orgId: '', // TODO: specify selected org
					storeName: settings.storeName,
					settings,
				},
			}),
		[onCartPageView, router.asPath, settings, user]
	);

	const onCartViewEvent = useCallback(
		(eventData: EventsContextType['eventData']) => {
			if (data) {
				const { orderItem: orderItems } = data;
				if (!orderItems?.length) {
					onEmptyCart();
				} else {
					const contextData: Record<string, GTMCartViewContextData> = eventData['onCartView'];
					const allFetched = orderItems.every(
						({ orderItemId }) =>
							contextData?.[orderItemId]?.product && contextData[orderItemId].category
					);
					orderItems?.map((oi : OrderItem ) => {							
						if(oi && contextData && contextData[oi.orderItemId] && contextData[oi.orderItemId].breadcrumb){
							oi.item_category = contextData[oi.orderItemId].breadcrumb?.at(0)?.label;
							oi.item_category2 = contextData[oi.orderItemId].breadcrumb?.at(1)?.label;
							oi.item_category3 = contextData[oi.orderItemId].breadcrumb?.at(2)?.label;
						}
					} );
					if (allFetched) {
						onCartView({
							gtm: {
								cart: data,
								contextData,
								orgName: '', // TODO: specify selected org-name
								orgId: '', // TODO: specify selected org
								storeName: settings.storeName,
								settings,
							},
						});
					}
				}
			}
		},
		[data, onCartView, onEmptyCart, settings]
	);

	useEffect(() => {
		// old invocations of cart may arrive later than newer ones -- here, we update `data`
		//   only if `lastUpdateDate` is greater than or equal to a previously stored value --
		//   ideally we should only update on greater than values, but updates within the same
		//   millisecond time-frame are still possible so we will allow that -- alternatively
		//   we could do a more granular check of the cart, e.g., check orderItem counts, check
		//   quantity counts, partNumber differences, etc. but that is probably not warranted
		if (
			(isEmpty(data) || (data as any)?._obfuscated) ||
			isEmpty(cart) ||
			cart.lastUpdateDate >= data.lastUpdateDate ||
			cart.orgUniqueID !== data.orgUniqueID || 
			cart.totalAdjustment !== data.totalAdjustment || 
			cart.grandTotal !== data.grandTotal
		) {
			setData(cart);
		}
		if (cart === undefined) {
			setData({} as Order);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cart]);

	const [pickupOrderItems, deliveryOrderItems] = useMemo(
		() => partition(cart?.orderItem ?? [], (e) => e.shipModeCode === SHIP_MODE_CODE_PICKUP),
		[cart]
	);

	const { pickupOnly, deliveryOnly } = useMemo(
		() => ({
			pickupOnly:
				cart?.orderItem &&
				!cart.orderItem.some((item) => item && item.shipModeCode !== SHIP_MODE_CODE_PICKUP),
			deliveryOnly:
				cart?.orderItem &&
				!cart.orderItem.some((item) => item && item.shipModeCode === SHIP_MODE_CODE_PICKUP),
		}),
		[cart]
	);

	const physicalStoreIdInCart = useMemo(
		() =>
			// assume pickup items from same store
			pickupOrderItems.at(0)?.physicalStoreId,
		[pickupOrderItems]
	);

	const pickupOrderItemsFromOtherStore = useMemo(
		// this can happen when merge cart from a guest shopper
		() => pickupOrderItems.filter((item) => item.physicalStoreId !== physicalStoreIdInCart),
		[physicalStoreIdInCart, pickupOrderItems]
	);
	
	const {
		showKlarna,
		showOrderMinMessage
	} = useMemo(() => {
		const configuredOrderMinimumCents = (parseFloat(settings.userData.orderActualCheckoutMinimum) ?? '00') * 100;
		const orderGrandTotal = parseFloat(data?.grandTotal ?? '00') * 100;
		const orderTotalShippping = parseFloat(data?.totalShippingCharge ?? '00') * 100;
		const grandTotalNoShipCharge = orderGrandTotal - orderTotalShippping;
		return {
			showKlarna: (grandTotalNoShipCharge < configuredOrderMinimumCents || isOrderHasPresaleItem || data?.x_field3 === 'pickUp') ? false : true,
			showOrderMinMessage: grandTotalNoShipCharge < configuredOrderMinimumCents,
		}
	}, [
		data?.grandTotal,
		data?.totalShippingCharge,
		data?.x_field3,
		isOrderHasPresaleItem
	]);

	const legoLoyaltyCartValues: LegoLoyaltyCartValues = useMemo(() => {
		const isTreazurePromoLoyaltyEnabled =
			settings.userData.Treazure_PromoEngine_Loyalty_AccountCheck_Enabled === 'true';
	
		const defaultValues: LegoLoyaltyCartValues = {
			legoLoyaltyOrderItem: undefined,
			savedLoyaltyAccNrT: '',
			isNewAccNrRequestedT: false,
			isTreazurePromoLoyaltyEnabled: false,
			isPointBasedLegoLoyaltyEnabled: false,
		};
	
		if (!isTreazurePromoLoyaltyEnabled || !data?.orderItem?.length || !productsInOrder?.length) {
			return defaultValues;
		}
	
		const legoLoyaltyOrderItemT = data?.orderItem?.at(0) as OrderItem;

		const isPointBasedLegoLoyaltyEnabled = parseInt(data?.x_loyaltyPointsEarned??'0')>0;
	
		const isNewAccNrRequestedT = data.orderExtendAttribute?.some(
			oea => oea.attributeName === "NewLoyaltyNumberRequested" && oea.attributeValue === "true"
		) ?? false;
	
		const savedLoyaltyAccNrT = data.orderExtendAttribute?.find(
			oea => oea.attributeName === "LoyaltyAccountNumber"
		)?.attributeValue ?? EMPTY_STRING;
	
		return {
			legoLoyaltyOrderItem: legoLoyaltyOrderItemT,
			isNewAccNrRequestedT,
			savedLoyaltyAccNrT,
			isTreazurePromoLoyaltyEnabled,
			isPointBasedLegoLoyaltyEnabled
		};
	}, [data?.orderItem, data?.orderExtendAttribute, productsInOrder]);

	const freeItemArticleNumberRemoved = useMemo(() => {
		const value = data?.orderExtendAttribute?.find(
			a => a.attributeName === "FreeItemArticleNumberRemoved" && a.attributeValue
		)?.attributeValue;

		return value ? value.split(",").filter(Boolean) : [];
	}, [data?.orderExtendAttribute]);

	const donateItemPartNumber = settings.userData.DonateItemPartNumber;

	const freeArticlesToAdd = useMemo(() => {
		const orderItems = data?.orderItem ?? [];
		const alreadyHasDonation = orderItems.some(
			oi => oi.partNumber === donateItemPartNumber
		);

		if (orderItems.length === 0) {
			return [];
		}

		if(donateItemPartNumber){
			if(alreadyHasDonation){
				return freeItemArticleNumberRemoved;
			}else {
				const presaleArticles = getProductsWithAttribute(productsInOrder, "ITNL_ReleaseDate");
				const filteredOItems = orderItems?.filter(oi => oi.freeGift === "false" && oi.partNumber !== donateItemPartNumber);
				if(filteredOItems?.length > presaleArticles?.length){
					return [...freeItemArticleNumberRemoved, donateItemPartNumber];
				}else {
					return freeItemArticleNumberRemoved
				}
			}
		}else {
			return freeItemArticleNumberRemoved;
		}
	}, [data?.orderItem, donateItemPartNumber, freeItemArticleNumberRemoved, productsInOrder]);

	const addRemoveFreeItemToCart = useCallback(async(partNumber:string, action:string, freeItemType:string) => {
		try {
			const storeId = settings?.storeId??STORE_ID;
			
			if(freeItemType === FREE_GIFT){
				
				let tempFreeItemArticleNumberRemoved = [...freeItemArticleNumberRemoved];
				if(action===REMOVE){
					tempFreeItemArticleNumberRemoved.push(partNumber);
				}else{
					tempFreeItemArticleNumberRemoved = tempFreeItemArticleNumberRemoved.filter(p=>p!==partNumber);
				}
				
				const attributeValue = tempFreeItemArticleNumberRemoved?.length ? tempFreeItemArticleNumberRemoved.join(",") : EMPTY_STRING;
				const payload = {
					orderId: '.',
					orderItem: [data?.orderItem?.filter(oi=>oi.freeGift === "false")?.at(0) as OrderItem],
					orderExtendAttribute: [{
						attributeType: 'STRING',
						attributeName: 'FreeItemArticleNumberRemoved',
						attributeValue
					}],
				};

				await updateCartFetcher(true)(storeId, {}, payload, params);
				
				const calculationUsageId: any = ORDER_CONFIGS.calculationUsage.split(',');

				await transactionsCart(true).cartCalculateOrder1(
					storeId,
					undefined,
					{ calculationUsageId },
					params
				);
			}else if(freeItemType === DONATE_ITEM){
				if(!donateItemPartNumber)return;
				if(action === REMOVE){
					const orderItem = data?.orderItem?.find(oi=>oi.partNumber === donateItemPartNumber);
					if(!orderItem)return;
					orderItem.quantity = "0";
					const payload = {
						orderId: '.',
						orderItem: [orderItem],
					};
					await updateCartFetcher(true)(storeId, {}, payload, params);
				}
			}
			mutate(data);
		} catch (error) {
			console.error("Error updating FreeItemArticleNumberSelected value to ord ext attr table, error : ", error);
		}
	},[data, freeItemArticleNumberRemoved]);

	useEffect(() => { // This is to delete donation item fro the cart when cart becomes empty
		if (!donateItemPartNumber) return;

		const orderItems = data?.orderItem ?? [];

		const hasDonationItem = orderItems.some(
			oi => oi.partNumber === donateItemPartNumber
		);

		if (hasDonationItem && orderItems.length === 1) {
			addRemoveFreeItemToCart(donateItemPartNumber, REMOVE, DONATE_ITEM);
		}
	}, [data?.orderItem, donateItemPartNumber, addRemoveFreeItemToCart]);
	
	return {
		data,
		orderItems: data?.orderItem as OrderItem[],
		rewardOptions: data?.rewardOption as CartRewardOption[],
		mutateCart: mutate,
		count,
		onFullCartCheckout,
		checkout,
		canContinue,
		continueShopping,
		onPromoCodeRemove,
		onPromoCodeApply,
		onPromoCodeApplyByKey,
		onPromoCodeChange,
		onResetPromoCodeError,
		promoCode,
		loading: !error && !data,
		// the `loading` is against data state, which does not reflect the true loading state of cart.
		error,
		getCount,
		onCartPageViewEvent,
		onCartViewEvent,
		physicalStoreIdInCart,
		pickupOrderItemsFromOtherStore,
		pickupOrderItems,
		deliveryOrderItems,
		isFetchingCart: isValidating || isLoading,
		pickupOnly,
		deliveryOnly,
		showKlarna,
		showOrderMinMessage,
		legoLoyaltyCartValues,
		freeItemArticleNumberRemoved,
		freeArticlesToAdd,
		donateItemPartNumber,
		addRemoveFreeItemToCart,
		isOrderHasPickupOnlyItem,
		isOrderHasPresaleItem
	};
};
export { getCart };